import React, {createRef, ReactNode, RefObject} from "react";
import {Button} from "primereact/button";
import {UploadDialogDefinition} from "../dialog/components/document/UploadDialog";
import PortalDocument from "../document/PortalDocument";
import DocumentList, {DocumentListGlobals} from "./DocumentList";
import {DeleteDialogDefinition} from "../dialog/components/document/DeleteDialog";
import Parameter from "../dialog/parameter/Parameter";
import Downloader from "../document/Downloader";
import Dropzone, {DropzoneGlobals, DropzoneMode} from "./Dropzone";
import {RenameDialogDefinition} from "../dialog/components/document/RenameDialog";
import PortalDocumentManager, {PortalDocumentManagerGlobals} from "../document/PortalDocumentManager";
import {PdfaDialogDefinition} from "../dialog/components/convert/PdfaDialog";
import {OcrDialogDefinition} from "../dialog/components/convert/OcrDialog";
import {ConvertDialogDefinition} from "../dialog/components/convert/ConvertDialog";
import {MergeDialogDefinition} from "../dialog/components/document/MergeDialog";
import {PortfolioCreateDialogDefinition} from "../dialog/components/portfolio/PortfolioCreateDialog";
import News from "./News";
import {PortalDialogsGlobals} from "../dialog/PortalDialogs";
import {DocumentFilterGlobals} from "../menu/DocumentFilterComponent";
import {I18nGlobals} from "../../shared/translation/i18nInterface";
import {UrlConverterDialogDefinition} from "../dialog/components/document/UrlConverterDialog";
import MessageBox from "../../shared/components/MessageBox";
import FileHelper from "../../shared/helper/FileHelper";
import Exception from "../../shared/exception/Exception";
import MenuButton from "../../shared/menu/MenuButton";
import {MessageType} from "../../shared/log/MessageInterface";
import {DeleteDocumentType} from "../../shared/models/DeleteDocumentType";
import {
	Information,
	Limits,
	News as PortalNews,
	UserCredentials
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {HttpRestResponse} from "@softvision/webpdf-wsclient-9-typescript/lib/http/HttpRestResponse";
import {DocumentSortingGlobals} from "../document/DocumentSorting";
import Translate from "../../shared/translation/Translate";
import {PortalGlobals} from "../Portal";
import {DebugGlobals} from "../dialog/components/settings/DebugDialog";
import StorageItem from "../../shared/StorageItem";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

export interface StartScreenProperties extends PortalGlobals, PortalDialogsGlobals, PortalDocumentManagerGlobals,
	DocumentFilterGlobals, DocumentSortingGlobals, I18nGlobals, DropzoneGlobals, DocumentListGlobals, DebugGlobals {
}

export interface StartScreenState {
	newsActive: boolean;
	exception?: Exception;
	dropzoneMode: DropzoneMode;
}

export default class StartScreen extends PortalPureComponent<StartScreenProperties, StartScreenState> {
	private resizeContainer: RefObject<HTMLDivElement>;

	constructor(props: Readonly<StartScreenProperties>) {
		super(props);

		let dropzoneMode: string | null = localStorage.getItem(StorageItem.DROPZONE);
		let newsActive: string | null = localStorage.getItem(StorageItem.NEWS);

		this.state = {
			newsActive: (newsActive === null ? "true" : newsActive) === "true",
			dropzoneMode: dropzoneMode === null ? DropzoneMode.UPLOAD : dropzoneMode as DropzoneMode
		};

		this.resizeContainer = createRef<HTMLDivElement>();
		this.onDrop = this.onDrop.bind(this);
	}

	/**
	 * Handle documents download button click
	 */
	private async downloadDocuments(mode?: "single" | "zip"): Promise<void> {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let documentSelection: Array<PortalDocument> = documentManager.getSelection();
		let downloader: Downloader = new Downloader(this.props.context.session.getToken()!);

		if (mode !== "zip" && (documentSelection.length === 1 || mode === "single")) {
			for (let selectedDocument of documentSelection) {
				await downloader.downloadDocument(
					selectedDocument.getSourceDocumentId() as string,
					selectedDocument.getDocumentFile()?.fileName as string
				);
			}
		} else {
			let entries: Array<{ name: string, extension?: string, data: any }> = [];

			for (let selectedDocument of documentSelection) {
				let response: HttpRestResponse = await documentManager.downloadDocument(
					selectedDocument.getSourceDocumentId()!
				);

				entries.push({
					name: selectedDocument.getDocumentFile()!.fileName!,
					extension: selectedDocument.getDocumentFile()?.fileExtension,
					data: await response.getData()
				});
			}

			await downloader.downloadDocuments(entries);
		}
	}

	private async onDrop(filelist: FileList): Promise<void> {
		let user: UserCredentials | undefined = this.props.context.user;
		let portalDocuments: Array<PortalDocument> = this.props.context.documentManager.getDocuments();
		let maxWorkspace: number = FunctionHelper.applyValue(
			user?.userLimits?.diskSpaceLimit, Limits.getDiskSpaceLimitDefault()
		) * 1024 * 1024;
		let uploadLimit: number = FunctionHelper.applyValue(
			user?.userLimits?.uploadLimit, Limits.getUploadLimitDefault()
		) * 1024 * 1024;

		let currentWorkspace: number = 0;
		for (let portalDocument of portalDocuments) {
			currentWorkspace += FunctionHelper.applyValue(portalDocument.getDocumentFile()?.fileSize, 0);
		}

		let addedWorkspace: number = 0;
		for (let file of filelist) {
			if (uploadLimit > 0 && file.size > uploadLimit) {
				let exception: Exception = new Exception(
					Translate.value("errors.upload.size", {ns: "message"})
				);
				this.setState({
					exception: exception
				});

				return;
			}

			addedWorkspace += file.size;
		}

		if (maxWorkspace > 0 && currentWorkspace + addedWorkspace > maxWorkspace) {
			let exception: Exception = new Exception(
				Translate.value("errors.upload.workspace", {ns: "message"})
			);
			this.setState({
				exception: exception
			});

			return;
		}

		let maxDocuments: number = FunctionHelper.applyValue(user?.userLimits?.maxFiles, Limits.getMaxFilesDefault());
		if (maxDocuments > 0 && portalDocuments.length + filelist.length > maxDocuments) {
			let exception: Exception = new Exception(
				Translate.value("errors.upload.documents", {ns: "message"})
			);
			this.setState({
				exception: exception
			});

			return;
		}

		this.props.context.getPage().setState({
			showUploadDialog: true,
			uploadFiles: filelist
		});
	}

	private renderDocumentMenu(): ReactNode {
		let portalInfo: Information = this.props.context.info;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();
		let dropzone: RefObject<HTMLInputElement> = createRef<HTMLInputElement>();

		let selectionLocked: boolean = false;
		for (let selectedDocument of selection) {
			if (selectedDocument.isLocked()) {
				selectionLocked = true;
				break;
			}
		}

		return (
			<>
				<div className="functions" data-help="global.screen.document.functions" data-position="bottom">
					{
						(
							!this.props.documentChecks || (
								!UploadDialogDefinition.isEnabled(portalInfo) &&
								UrlConverterDialogDefinition.isEnabled(this.props.context.info)
							)
						) &&
						<MenuButton {...this.props} icon="icon icon-download multiple"
									onClick={() => {
										this.props.context.getPage().setState({
											showUrlConverterDialog: true
										});
									}}
									label={Translate.value("filemanager.open.url", {ns: "menu"})}/>
					}
					<Dropzone {...this.props} context={this.props.context} className="hidden" inputRef={dropzone}
							  onError={(exception: Exception) => {
								  this.setState({
									  exception: exception
								  });
							  }}
							  onUploadStart={(files) => {
								  this.props.context.getPage().setState({
									  showUploadDialog: true,
									  uploadFiles: files
								  });
							  }}/>
					{(!this.props.documentChecks || UploadDialogDefinition.isEnabled(portalInfo)) &&
						<MenuButton {...this.props} icon="icon icon-upload multiple" splitButton={true}
									className="start-screen-upload"
									disabled={
										this.props.documentChecks &&
										!UploadDialogDefinition.isActive(
											selection, this.props.context.info, this.props.signatureEditProfile
										)
									}
									label={Translate.value("filemanager.open.upload", {ns: "menu"})}
									onClick={() => this.props.context.getPage().setState({
										dropzoneMode: DropzoneMode.UPLOAD,
										showUploadDialog: true,
										uploadFiles: undefined
									})}
									menuItems={[
										{
											label: Translate.value(
												"filemanager.open.convert", {ns: "menu"}
											),
											icon: 'icon icon-convert multiple',
											disabled: (
												this.props.documentChecks &&
												!ConvertDialogDefinition.isEnabled(this.props.context.info)
											),
											command: () => {
												this.props.context.getPage().setState({
													dropzoneMode: DropzoneMode.CONVERT
												});

												if (dropzone.current !== null) {
													dropzone.current.click();
												}
											}
										},
										{
											label: Translate.value(
												"filemanager.open.archive", {ns: "menu"}
											),
											icon: 'icon icon-archive multiple',
											disabled: (
												this.props.documentChecks &&
												!PdfaDialogDefinition.isEnabled(this.props.context.info)
											),
											command: () => {
												this.props.context.getPage().setState({
													dropzoneMode: DropzoneMode.ARCHIVE
												});

												if (dropzone.current !== null) {
													dropzone.current.click();
												}
											}
										},
										{
											label: Translate.value(
												"filemanager.open.ocr", {ns: "menu"}
											),
											icon: 'icon icon-ocr multiple',
											disabled: (
												this.props.documentChecks &&
												!OcrDialogDefinition.isEnabled(this.props.context.info)
											),
											command: () => {
												this.props.context.getPage().setState({
													dropzoneMode: DropzoneMode.OCR
												});

												if (dropzone.current !== null) {
													dropzone.current.click();
												}
											}
										},
										{
											label: Translate.value(
												"filemanager.open.url", {ns: "menu"}
											),
											icon: 'icon icon-globe multiple',
											disabled: (
												this.props.documentChecks &&
												!UrlConverterDialogDefinition.isEnabled(this.props.context.info)
											),
											command: () => {
												this.props.context.getPage().setState({
													showUrlConverterDialog: true
												});
											}
										}
									]}/>
					}
					{(!this.props.documentChecks || MergeDialogDefinition.isEnabled(portalInfo)) &&
						<MenuButton {...this.props} icon="icon icon-merge multiple" className="start-screen-merge"
									disabled={
										this.props.documentChecks &&
										!MergeDialogDefinition.isActive(
											selection, this.props.context.info, this.props.signatureEditProfile
										)
									}
									label={Translate.value("filemanager.merge", {ns: "menu"})}
									onClick={() => {
										this.props.context.getPage().setState({
											showMergeDialog: true,
											mergeParameter: undefined
										});
									}}/>
					}
					<MenuButton {...this.props} disabled={selection.length === 0 || selectionLocked} splitButton={true}
								icon="icon icon-download multiple" onClick={() => this.downloadDocuments()}
								label={Translate.value("file.download.label", {ns: "menu"})}
								className="start-screen-download"
								menuItems={[
									{
										label: Translate.value("file.download.single", {ns: "menu"}),
										command: () => this.downloadDocuments("single")
									},
									{
										label: Translate.value("file.download.zip", {ns: "menu"}),
										command: () => this.downloadDocuments("zip")
									}
								]}/>
					{(!this.props.documentChecks || DeleteDialogDefinition.isEnabled(portalInfo)) &&
						<MenuButton {...this.props}
									disabled={
										this.props.documentChecks &&
										!DeleteDialogDefinition.isActive(
											selection, this.props.context.info, this.props.signatureEditProfile
										)
									}
									icon="icon icon-delete multiple" className="start-screen-delete"
									label={Translate.value("filemanager.delete", {ns: "menu"})}
									onClick={() => {
										let selection: Array<PortalDocument> = documentManager.getSelection();
										let documentIds: Array<string> = [];

										for (let portalDocument of selection) {
											documentIds.push(portalDocument.getSourceDocumentId()!);
										}

										let parameter: Parameter<DeleteDocumentType> =
											new Parameter<DeleteDocumentType>(
												DeleteDocumentType.fromJson({
													documentIds: documentIds
												} as DeleteDocumentType)
											);

										this.props.context.getPage().setState({
											showDeleteDialog: true,
											deleteParameter: parameter
										});
									}}/>
					}
					{(!this.props.documentChecks || RenameDialogDefinition.isEnabled(portalInfo)) &&
						<MenuButton {...this.props} className="start-screen-rename"
									disabled={
										this.props.documentChecks &&
										!RenameDialogDefinition.isActive(
											selection, this.props.context.info, this.props.signatureEditProfile
										)
									}
									icon="icon icon-rename multiple"
									label={Translate.value("filemanager.rename", {ns: "menu"})}
									onClick={() => {
										this.props.context.getPage().setState({
											showRenameDialog: true,
											renameParameter: undefined
										});
									}}/>
					}
					{(
							!this.props.documentChecks ||
							ConvertDialogDefinition.isEnabled(portalInfo) ||
							PdfaDialogDefinition.isEnabled(portalInfo) ||
							OcrDialogDefinition.isEnabled(portalInfo) ||
							PortfolioCreateDialogDefinition.isEnabled(portalInfo)
						) &&
						<div className="separator"/>
					}
					{(!this.props.documentChecks || ConvertDialogDefinition.isEnabled(portalInfo)) &&
						<MenuButton {...this.props} icon="icon icon-convert multiple" className="start-screen-convert"
									disabled={
										this.props.documentChecks &&
										!ConvertDialogDefinition.isActive(
											selection, this.props.context.info, this.props.signatureEditProfile
										)
									}
									label={Translate.value("filemanager.convert", {ns: "menu"})}
									onClick={() => {
										this.props.context.getPage().setState({
											showConvertDialog: true,
											convertParameter: undefined
										});
									}}/>
					}
					{(!this.props.documentChecks || PdfaDialogDefinition.isEnabled(portalInfo)) &&
						<MenuButton {...this.props} icon="icon icon-archive multiple" className="start-screen-archive"
									disabled={
										this.props.documentChecks &&
										!PdfaDialogDefinition.isActive(
											selection, this.props.context.info, this.props.signatureEditProfile
										)
									}
									label={Translate.value("filemanager.archive", {ns: "menu"})}
									onClick={() => {
										this.props.context.getPage().setState({
											showPdfaDialog: true,
											pdfaParameter: undefined
										});
									}}/>
					}
					{(!this.props.documentChecks || OcrDialogDefinition.isEnabled(portalInfo)) &&
						<MenuButton {...this.props} icon="icon icon-ocr multiple" className="start-screen-ocr"
									disabled={
										this.props.documentChecks &&
										!OcrDialogDefinition.isActive(
											selection, this.props.context.info, this.props.signatureEditProfile
										)
									}
									label={Translate.value("filemanager.ocr", {ns: "menu"})}
									onClick={() => {
										this.props.context.getPage().setState({
											showOcrDialog: true,
											ocrParameter: undefined
										});
									}}/>
					}
					{(!this.props.documentChecks || PortfolioCreateDialogDefinition.isEnabled(portalInfo)) &&
						<MenuButton {...this.props} icon="icon icon-portfolio multiple"
									className="start-screen-portfolio"
									disabled={
										this.props.documentChecks &&
										!PortfolioCreateDialogDefinition.isActive(
											selection, this.props.context.info, this.props.signatureEditProfile
										)
									}
									label={Translate.value("filemanager.portfolio", {ns: "menu"})}
									onClick={() => {
										this.props.context.getPage().setState({
											showPortfolioCreateDialog: true,
											portfolioCreateParameter: undefined
										});
									}}/>
					}
				</div>
			</>
		)
	}

	private renderDocumentPanel(): ReactNode {
		let portalInfo: Information = this.props.context.info;
		let user: UserCredentials | undefined = this.props.context.user;
		let uploadLimit: number = FunctionHelper.applyValue(
			user?.userLimits?.uploadLimit, Limits.getUploadLimitDefault()
		);
		let error: Exception | undefined = this.state.exception;

		if (!UploadDialogDefinition.isEnabled(portalInfo) &&
			!UrlConverterDialogDefinition.isEnabled(this.props.context.info)) {
			return (
				<MessageBox {...this.props} type={MessageType.DANGER}>
					<Translate {...this.props} data="startscreen.panels.documentmanager.upload_impossible"
							   namespace="screen"/>
				</MessageBox>
			);
		}

		let dropzoneLabel: string = Translate.value("filemanager.open.upload", {ns: "menu"});
		let dropzoneIcon: string = "icon-upload";
		let dropzoneMessage: string = Translate.value(
			"startscreen.panels.documentmanager.dropzone.upload", {ns: "screen"}
		);
		switch (this.state.dropzoneMode) {
			case DropzoneMode.CONVERT:
				dropzoneLabel = Translate.value("filemanager.open.convert", {ns: "menu"});
				dropzoneIcon = "icon-convert";
				dropzoneMessage = Translate.value(
					"startscreen.panels.documentmanager.dropzone.convert", {ns: "screen"}
				);
				break;
			case DropzoneMode.ARCHIVE:
				dropzoneLabel = Translate.value("filemanager.open.archive", {ns: "menu"});
				dropzoneIcon = "icon-archive";
				dropzoneMessage = Translate.value(
					"startscreen.panels.documentmanager.dropzone.archive", {ns: "screen"}
				);
				break;
			case DropzoneMode.OCR:
				dropzoneLabel = Translate.value("filemanager.open.ocr", {ns: "menu"});
				dropzoneIcon = "icon-ocr";
				dropzoneMessage = Translate.value(
					"startscreen.panels.documentmanager.dropzone.ocr", {ns: "screen"}
				);
				break;
		}

		return (
			<>
				{this.renderDocumentMenu()}
				{FunctionHelper.exists(error) &&
					<MessageBox type={MessageType.DANGER} closeable={true} language={this.props.language}
								onClose={() => this.setState({
									exception: undefined
								})}
								stacktrace={
									<>
										<p>{error!.message} ({error!.code})</p>
										<pre>{error!.stack}</pre>
									</>
								}>
						{error!.message}
					</MessageBox>
				}
				{this.props.portalDocuments.get().length === 0 ?
					<>
						{(!this.props.documentChecks || UploadDialogDefinition.isEnabled(portalInfo)) &&
							<div data-help="global.screen.document.dropzone" data-position="top">
								<Dropzone {...this.props} context={this.props.context}
										  onError={(exception: Exception) => {
											  this.setState({
												  exception: exception
											  });
										  }}
										  onUploadStart={(files) => {
											  this.props.context.getPage().setState({
												  dropzoneMode: this.state.dropzoneMode,
												  showUploadDialog: true,
												  uploadFiles: files
											  });
										  }}>
									<p>
										<Translate {...this.props}
												   data="startscreen.panels.documentmanager.dropzone.description"
												   namespace="screen" values={{mode: dropzoneMessage}}/>
										<br/><br/>
										<Button label={Translate.value("buttons.browse")}/>
										<br/><br/>
										{uploadLimit !== 0 &&
											<Translate {...this.props}
													   data="startscreen.panels.documentmanager.dropzone.maxsize"
													   namespace="screen"
													   values={{
														   size: FileHelper.formatFileSize(uploadLimit * 1024 * 1024)
													   }}/>
										}
									</p>
									<MenuButton {...this.props} className="dropzone-mode-dropdown" splitButton={true}
												icon={"icon small " + dropzoneIcon} label={dropzoneLabel}
												menuItems={
													[
														{
															label: Translate.value(
																"startscreen.panels.documentmanager.dropzone.mode.upload",
																{ns: "screen"}
															),
															icon: 'icon icon-upload small',
															data: DropzoneMode.UPLOAD,
															disabled: (
																this.props.documentChecks &&
																!UploadDialogDefinition.isEnabled(this.props.context.info)
															),
															command: () => {
																this.setState({
																	dropzoneMode: DropzoneMode.UPLOAD
																}, () => {
																	localStorage.setItem(
																		StorageItem.DROPZONE, this.state.dropzoneMode
																	);
																});
															}
														},
														{
															label: Translate.value(
																"startscreen.panels.documentmanager.dropzone.mode.convert",
																{ns: "screen"}
															),
															icon: 'icon icon-convert small',
															data: DropzoneMode.CONVERT,
															disabled: (
																this.props.documentChecks &&
																!ConvertDialogDefinition.isEnabled(this.props.context.info)
															),
															command: () => {
																this.setState({
																	dropzoneMode: DropzoneMode.CONVERT
																}, () => {
																	localStorage.setItem(
																		StorageItem.DROPZONE, this.state.dropzoneMode
																	);
																});
															}
														},
														{
															label: Translate.value(
																"startscreen.panels.documentmanager.dropzone.mode.archive",
																{ns: "screen"}
															),
															icon: 'icon icon-archive small',
															data: DropzoneMode.ARCHIVE,
															disabled: (
																this.props.documentChecks &&
																!PdfaDialogDefinition.isEnabled(this.props.context.info)
															),
															command: () => {
																this.setState({
																	dropzoneMode: DropzoneMode.ARCHIVE
																}, () => {
																	localStorage.setItem(
																		StorageItem.DROPZONE, this.state.dropzoneMode
																	);
																});
															}
														},
														{
															label: Translate.value(
																"startscreen.panels.documentmanager.dropzone.mode.ocr",
																{ns: "screen"}
															),
															icon: 'icon icon-ocr small',
															data: DropzoneMode.OCR,
															disabled: (
																this.props.documentChecks &&
																!OcrDialogDefinition.isEnabled(this.props.context.info)
															),
															command: () => {
																this.setState({
																	dropzoneMode: DropzoneMode.OCR
																}, () => {
																	localStorage.setItem(
																		StorageItem.DROPZONE, this.state.dropzoneMode
																	);
																});
															}
														}
													]}/>
								</Dropzone>
							</div>
						}
					</> :
					<DocumentList {...this.props} onDrop={(event: React.DragEvent<HTMLDivElement>) => {
						if (this.props.documentChecks && !UploadDialogDefinition.isEnabled(portalInfo)) {
							return;
						}

						this.setState({
							dropzoneMode: DropzoneMode.UPLOAD
						}, () => {
							localStorage.setItem(StorageItem.DROPZONE, this.state.dropzoneMode);
						});

						this.onDrop(event.dataTransfer.files)
					}}/>
				}
			</>
		)
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let portalInfo: Information = this.props.context.info;
		let newsSettings: PortalNews | undefined = portalInfo.newsPanel;
		let hasNews: boolean = FunctionHelper.exists(newsSettings) && newsSettings!.entry!.length > 0;

		return (
			<div className="start-screen" ref={this.resizeContainer}>
				{hasNews &&
					<>
						<News {...this.props} newsActive={this.state.newsActive} startScreen={this.resizeContainer}
							  toggleNews={() => this.setState({
								  newsActive: !this.state.newsActive
							  })}/>
						<Button className="toggle-btn left" icon="icon icon-angle-right"
								style={!this.state.newsActive ? {} : {display: "none"}}
								onClick={() => {
									localStorage.removeItem(StorageItem.NEWS);
									this.setState({newsActive: !this.state.newsActive});
								}}/>
					</>
				}
				<div className="screen">
					<div className="body">
						<h2>
							<Translate {...this.props} data="startscreen.panels.documentmanager.title"
									   namespace="screen"/>
						</h2>
						{this.renderDocumentPanel()}
					</div>
				</div>
			</div>
		);
	}
}